import { useState, useEffect } from "react";
import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Loader2,
  Shield,
  XCircle,
  CheckCircle,
  Info,
} from "lucide-react";
import { fetchWithAuth } from "@/lib/api/client";

// Tipos para el resultado del detector de riesgos
interface Risk {
  id: string;
  category: string;
  severity: "CRÍTICO" | "ALTO" | "MEDIO" | "BAJO";
  title: string;
  clause_reference: string;
  clause_text: string;
  analysis: string;
  impact: string;
  recommendation: string;
  negotiable: boolean;
  precedent?: string;
}

interface RiskMatrix {
  CRÍTICO: number;
  ALTO: number;
  MEDIO: number;
  BAJO: number;
}

interface RiskDetectorResult {
  risk_score: number;
  risk_level: "CRÍTICO" | "ALTO" | "MEDIO" | "BAJO";
  summary: string;
  risks: Risk[];
  risk_matrix: RiskMatrix;
  go_no_go_recommendation: "PROCEDER" | "PROCEDER CON CAUTELA" | "NO PROCEDER";
  go_no_go_rationale: string;
}

interface RiskDetectorViewProps {
  licitacionId: string;
  initialResult?: RiskDetectorResult | null;
}

const severityConfig = {
  CRÍTICO: {
    icon: XCircle,
    color: "text-red-600",
    bg: "bg-red-100",
    border: "border-red-200",
    label: "Crítico",
  },
  ALTO: {
    icon: AlertTriangle,
    color: "text-orange-600",
    bg: "bg-orange-100",
    border: "border-orange-200",
    label: "Alto",
  },
  MEDIO: {
    icon: AlertCircle,
    color: "text-yellow-600",
    bg: "bg-yellow-100",
    border: "border-yellow-200",
    label: "Medio",
  },
  BAJO: {
    icon: Info,
    color: "text-blue-600",
    bg: "bg-blue-100",
    border: "border-blue-200",
    label: "Bajo",
  },
};

const goNoGoConfig = {
  PROCEDER: {
    icon: CheckCircle,
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
  },
  "PROCEDER CON CAUTELA": {
    icon: AlertTriangle,
    color: "text-yellow-600",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
  },
  "NO PROCEDER": {
    icon: XCircle,
    color: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
  },
};

export default function RiskDetectorView({ licitacionId, initialResult }: RiskDetectorViewProps) {
  const [sensitivity, setSensitivity] = useState<"low" | "medium" | "high">("medium");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<RiskDetectorResult | null>(null);
  const [expandedRisks, setExpandedRisks] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Solo aceptar resultados con la forma esperada del detector de riesgos.
    // Si la ejecución viene de otro agente, descartar para evitar crashes en
    // .filter/.map sobre `risks` undefined.
    if (initialResult && Array.isArray(initialResult.risks)) {
      setResult(initialResult);
      const criticalAndHigh = initialResult.risks
        .filter((r: Risk) => r.severity === "CRÍTICO" || r.severity === "ALTO")
        .map((r: Risk) => r.id);
      setExpandedRisks(new Set(criticalAndHigh));
    }
  }, [initialResult]);

  const availableCategories = [
    "PENALIZACIONES",
    "PLAZOS",
    "ECONÓMICO",
    "RESPONSABILIDAD",
    "SUBCONTRATACIÓN",
    "PROPIEDAD INTELECTUAL",
    "GARANTÍAS",
    "MODIFICACIONES",
    "RESOLUCIÓN",
    "AMBIGÜEDADES",
  ];

  const sensitivityLabels = {
    low: "Bajo (solo críticos y altos)",
    medium: "Medio (críticos, altos y medios)",
    high: "Alto (todos los riesgos)",
  };

  const toggleRisk = (riskId: string) => {
    setExpandedRisks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(riskId)) {
        newSet.delete(riskId);
      } else {
        newSet.add(riskId);
      }
      return newSet;
    });
  };

  const handleAnalyze = async () => {
    setIsLoading(true);
    try {
      const data = await fetchWithAuth(`/api/v1/agents/risk_detector/execute`, {
        method: "POST",
        body: JSON.stringify({
          licitacion_id: licitacionId,
          input_data: {
            sensitivity,
            risk_categories: selectedCategories.length > 0 ? selectedCategories : undefined,
          },
        }),
      });
      setResult(data.result);
      // Expandir todos los riesgos críticos y altos por defecto
      const criticalAndHighRisks = data.result.risks
        .filter((r: Risk) => r.severity === "CRÍTICO" || r.severity === "ALTO")
        .map((r: Risk) => r.id);
      setExpandedRisks(new Set(criticalAndHighRisks));
    } catch (error) {
      console.error("Error:", error);
      alert("Error al analizar riesgos. Por favor, intenta nuevamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category) ? prev.filter((c) => c !== category) : [...prev, category]
    );
  };

  return (
    <div className="space-y-6">
      {/* Descripción del agente */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-orange-800">
          <p className="font-semibold mb-1">Detector de Riesgos</p>
          <p>
            Examina las cláusulas del pliego para detectar riesgos legales, técnicos
            y económicos: penalizaciones desproporcionadas, plazos agresivos,
            responsabilidades excesivas, ambigüedades contractuales, etc. Incluye una
            recomendación Go/No-Go basada en el nivel de riesgo global.
          </p>
        </div>
      </div>

      {/* Formulario */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">
          Configuración del Análisis de Riesgos
        </h3>

        {/* Nivel de sensibilidad */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nivel de Sensibilidad
          </label>
          <div className="flex gap-2">
            {(["low", "medium", "high"] as const).map((level) => (
              <button
                key={level}
                onClick={() => setSensitivity(level)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  sensitivity === level
                    ? "bg-blue-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {sensitivityLabels[level]}
              </button>
            ))}
          </div>
        </div>

        {/* Categorías de riesgo */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Categorías de Riesgo (opcional - todas por defecto)
          </label>
          <div className="flex flex-wrap gap-2">
            {availableCategories.map((category) => (
              <button
                key={category}
                onClick={() => toggleCategory(category)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  selectedCategories.includes(category)
                    ? "bg-orange-500 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Botón de acción */}
        <button
          onClick={handleAnalyze}
          disabled={isLoading}
          className="w-full bg-orange-500 text-white font-medium py-2 px-4 rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Analizando riesgos...
            </>
          ) : (
            <>
              <Shield className="w-5 h-5 mr-2" />
              Analizar Riesgos
            </>
          )}
        </button>
      </div>

      {/* Resultados */}
      {result && (
        <div className="space-y-6">
          {/* Risk Score y Nivel de Riesgo */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Evaluación General</h3>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-sm text-gray-500 mb-1">Risk Score</div>
                  <div
                    className={`text-3xl font-bold ${
                      result.risk_score >= 7
                        ? "text-red-600"
                        : result.risk_score >= 4
                        ? "text-yellow-600"
                        : "text-green-600"
                    }`}
                  >
                    {result.risk_score.toFixed(1)}/10
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-sm text-gray-500 mb-1">Nivel</div>
                  <div
                    className={`px-3 py-1 rounded-full text-sm font-bold ${
                      severityConfig[result.risk_level].bg
                    } ${severityConfig[result.risk_level].color}`}
                  >
                    {result.risk_level}
                  </div>
                </div>
              </div>
            </div>

            {/* Resumen */}
            <div className="bg-gray-50 rounded-md p-4 mb-4">
              <p className="text-sm text-gray-700">{result.summary}</p>
            </div>

            {/* Matriz de Riesgos */}
            <div className="grid grid-cols-4 gap-4">
              <div className="text-center">
                <div
                  className={`flex items-center justify-center mb-1 ${severityConfig.CRÍTICO.color}`}
                >
                  <XCircle className="w-5 h-5 mr-1" />
                  <span className="text-2xl font-bold">{result.risk_matrix.CRÍTICO}</span>
                </div>
                <p className="text-xs text-gray-600">Críticos</p>
              </div>
              <div className="text-center">
                <div
                  className={`flex items-center justify-center mb-1 ${severityConfig.ALTO.color}`}
                >
                  <AlertTriangle className="w-5 h-5 mr-1" />
                  <span className="text-2xl font-bold">{result.risk_matrix.ALTO}</span>
                </div>
                <p className="text-xs text-gray-600">Altos</p>
              </div>
              <div className="text-center">
                <div
                  className={`flex items-center justify-center mb-1 ${severityConfig.MEDIO.color}`}
                >
                  <AlertCircle className="w-5 h-5 mr-1" />
                  <span className="text-2xl font-bold">{result.risk_matrix.MEDIO}</span>
                </div>
                <p className="text-xs text-gray-600">Medios</p>
              </div>
              <div className="text-center">
                <div
                  className={`flex items-center justify-center mb-1 ${severityConfig.BAJO.color}`}
                >
                  <Info className="w-5 h-5 mr-1" />
                  <span className="text-2xl font-bold">{result.risk_matrix.BAJO}</span>
                </div>
                <p className="text-xs text-gray-600">Bajos</p>
              </div>
            </div>
          </div>

          {/* Recomendación Go/No-Go */}
          <div
            className={`rounded-lg border ${
              goNoGoConfig[result.go_no_go_recommendation].border
            } ${goNoGoConfig[result.go_no_go_recommendation].bg} p-6`}
          >
            <div className="flex items-start">
              {(() => {
                const GoNoGoIcon = goNoGoConfig[result.go_no_go_recommendation].icon;
                return (
                  <GoNoGoIcon
                    className={`w-6 h-6 mr-3 mt-0.5 flex-shrink-0 ${
                      goNoGoConfig[result.go_no_go_recommendation].color
                    }`}
                  />
                );
              })()}
              <div>
                <h3
                  className={`text-lg font-semibold mb-2 ${
                    goNoGoConfig[result.go_no_go_recommendation].color
                  }`}
                >
                  Recomendación: {result.go_no_go_recommendation}
                </h3>
                <p className="text-sm text-gray-700">{result.go_no_go_rationale}</p>
              </div>
            </div>
          </div>

          {/* Lista de riesgos */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800">
              Riesgos Identificados ({result.risks.length})
            </h3>
            {result.risks.map((risk) => {
              const isExpanded = expandedRisks.has(risk.id);
              const severityInfo = severityConfig[risk.severity];
              const SeverityIcon = severityInfo.icon;

              return (
                <div
                  key={risk.id}
                  className={`bg-white rounded-lg border ${severityInfo.border} overflow-hidden`}
                >
                  {/* Header del riesgo */}
                  <button
                    onClick={() => toggleRisk(risk.id)}
                    className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${severityInfo.bg} ${severityInfo.color} flex items-center`}
                      >
                        <SeverityIcon className="w-4 h-4 mr-1" />
                        {severityInfo.label}
                      </div>
                      <div className="text-left">
                        <div className="font-semibold text-gray-800">{risk.title}</div>
                        <div className="text-xs text-gray-500">{risk.category}</div>
                      </div>
                      {risk.negotiable && (
                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          Negociable
                        </span>
                      )}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>

                  {/* Contenido expandido */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 px-6 py-4 space-y-4">
                      {/* Referencia de cláusula */}
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-1">
                          Cláusula Referenciada
                        </h5>
                        <p className="text-sm text-gray-600 mb-1">{risk.clause_reference}</p>
                        <div className="bg-gray-50 rounded p-3 text-sm text-gray-700 italic border-l-4 border-gray-300">
                          "{risk.clause_text}"
                        </div>
                      </div>

                      {/* Análisis */}
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Análisis</h5>
                        <p className="text-sm text-gray-700">{risk.analysis}</p>
                      </div>

                      {/* Impacto */}
                      <div>
                        <h5 className="text-sm font-semibold text-gray-700 mb-1">Impacto</h5>
                        <p className="text-sm text-gray-700">{risk.impact}</p>
                      </div>

                      {/* Recomendación */}
                      <div className="bg-blue-50 rounded p-3 border-l-4 border-blue-400">
                        <h5 className="text-sm font-semibold text-blue-800 mb-1">
                          Recomendación
                        </h5>
                        <p className="text-sm text-blue-700">{risk.recommendation}</p>
                      </div>

                      {/* Precedente (opcional) */}
                      {risk.precedent && (
                        <div className="bg-purple-50 rounded p-3 border-l-4 border-purple-400">
                          <h5 className="text-sm font-semibold text-purple-800 mb-1">
                            Precedente Legal
                          </h5>
                          <p className="text-sm text-purple-700">{risk.precedent}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
